import React from 'react';

import { type StartingChatMessage } from '@eluve/blocks';

import { Chat } from './Chat';
import { type ChatView, useChatSession } from './useChatSession';

interface ChatConfig {
  /**
   * Optional when provided, enables patient-specific features
   */
  patientId?: string;
  /**
   * Optional appointment context — set when chat is opened from an appointment page
   */
  appointmentId?: string;
  /**
   * Starting common questions / chat templates
   */
  startingMessages: StartingChatMessage[];
}

interface ChatComponentProps {
  tenantId: string;
  baseUrl: string;
  config: ChatConfig;
  onStartersChanged?: () => void;
  onNoteUpdated?: () => void;
  onCustomInstructionSaved?: () => void;
  onDocumentAdded?: (doc?: { id: string; title: string }) => void;
  /** Custom headers for the SSE transport (e.g. Bearer auth in extension) */
  transportHeaders?:
    | Record<string, string>
    | Headers
    | (() => Promise<Record<string, string> | Headers>);
  /** Credentials mode for the SSE transport (defaults to 'include') */
  transportCredentials?:
    | RequestCredentials
    | (() => Promise<RequestCredentials>);
}

export const ChatComponent = ({
  tenantId,
  baseUrl,
  config,
  onStartersChanged,
  onNoteUpdated,
  onCustomInstructionSaved,
  onDocumentAdded,
  transportHeaders,
  transportCredentials,
}: ChatComponentProps) => {
  const session = useChatSession({
    tenantId,
    baseUrl,
    patientId: config.patientId,
    appointmentId: config.appointmentId,
    startingMessages: config.startingMessages,
    transportHeaders,
    transportCredentials,
    onStartersChanged,
    onNoteUpdated,
    onCustomInstructionSaved,
    onDocumentAdded,
  });

  return (
    <Chat
      tenantId={tenantId}
      patientId={config.patientId}
      appointmentId={config.appointmentId}
      view={session.view}
      currentThreadId={session.currentThreadId}
      draftMessage={session.draftMessage}
      status={session.status}
      onNewChat={session.startNewChat}
      onGoToHistory={session.goToHistory}
      onEnsureThread={session.ensureThread}
      onMessageSend={session.sendMessage}
      onSelectThread={session.selectThread}
      onStopStream={session.stopStream}
      setDraftMessage={session.setDraftMessage}
      startingMessages={session.startingMessages}
      threads={session.threads}
      onThreadTitleUpdate={session.updateThreadTitle}
      onStarterSaved={onStartersChanged}
      onNoteUpdated={onNoteUpdated}
      onCustomInstructionSaved={onCustomInstructionSaved}
      onDocumentAdded={onDocumentAdded}
      hasMore={session.hasMore}
      isLoadingMore={session.isLoadingMore}
      onLoadMore={session.loadMore}
      pdfManager={session.pdfManager}
      onSourceClick={session.handleSourceClick}
    />
  );
};

export type { ChatConfig, ChatComponentProps, ChatView };
